/**
 * ScrollWizardPage - One-page scroll wizard for tournament creation
 * Landing page style with vertical scroll navigation
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTournament } from '../contexts/TournamentContext';
import ConfigSection from './sections/ConfigSection';
import PlayersSection from './sections/PlayersSection';
import CourtsSection from './sections/CourtsSection';
import ScheduleSection from './sections/ScheduleSection';
import ResultsSection from './sections/ResultsSection';
import RankingSection from './sections/RankingSection';
import FinalSection from './sections/FinalSection';
import LeftNavMenu from './components/LeftNavMenu';
import SettingsSection from './sections/SettingsSection';
import './ScrollWizard.css';

const ScrollWizardPage = ({
  onTournamentsClick,
  onResetClick,
  onQRCodeClick,
  onExportPDFClick,
  onSignOutClick,
  onLoginClick,
  isSyncing,
  lastSyncTime
}) => {
  const { scheduleGenerated, schedule, config } = useTournament();

  const allRoundsComplete = scheduleGenerated &&
    schedule?.rounds?.length > 0 &&
    schedule.rounds.every(r => r.matches && r.matches.every(m => m.validated === true));
  const [currentSection, setCurrentSection] = useState(0);
  const [scrollSnapEnabled, setScrollSnapEnabled] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const sectionRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  // Sections configuration
  const sections = [
    { id: 'config', label: 'Configuration', component: ConfigSection, enabled: true },
    { id: 'players', label: 'Joueurs', component: PlayersSection, enabled: true },
    { id: 'courts', label: 'Terrains', component: CourtsSection, enabled: true },
    { id: 'schedule', label: 'Planning', component: ScheduleSection, enabled: scheduleGenerated },
    { id: 'results', label: 'Résultats', component: ResultsSection, enabled: scheduleGenerated },
    { id: 'ranking', label: 'Classement', component: RankingSection, enabled: scheduleGenerated },
    { id: 'final', label: 'Phase finale', component: FinalSection, enabled: allRoundsComplete && config?.hasFinalPhase !== false }
  ];

  // Intersection Observer to detect current section
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    // Use different rootMargin for mobile vs desktop
    const isMobile = window.innerWidth < 768;
    const rootMargin = isMobile ? '-20% 0px -20% 0px' : '-50% 0px -50% 0px';

    const options = {
      root: scrollContainerRef.current,
      rootMargin: rootMargin,
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          if (index !== -1) {
            setCurrentSection(index);
          }
        }
      });
    }, options);

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [scheduleGenerated]);

  // Scroll to section
  const scrollToSection = (index) => {
    if (sectionRefs.current[index]) {
      sectionRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle tournament generation
  const handleGenerateTournament = () => {
    // Tournament generation is handled by CourtsSection
    // After generation, scroll to Schedule
    // scheduleGenerated is now managed by TournamentContext
    setTimeout(() => {
      scrollToSection(3); // Scroll to Schedule section (now at index 3)
    }, 500);
  };

  // Enable scroll-snap on first scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollSnapEnabled && scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        if (scrollTop > 10) {
          setScrollSnapEnabled(true);
        }
      }
    };

    const container = scrollContainerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollSnapEnabled]);

  // Generate background class based on current section
  const sectionBackgrounds = {
    0: 'bg-section-config',
    1: 'bg-section-players',
    2: 'bg-section-courts',
    3: 'bg-section-schedule',
    4: 'bg-section-results',
    5: 'bg-section-ranking',
    6: 'bg-section-final'
  };
  const bgClass = sectionBackgrounds[currentSection] || 'bg-section-config';

  return (
    <div
      ref={scrollContainerRef}
      className={`scroll-wizard ${scrollSnapEnabled ? '' : 'scroll-snap-disabled'} ${bgClass}`}
    >
      {/* Left Navigation Menu */}
      <LeftNavMenu
        sections={sections}
        currentSection={currentSection}
        onNavigate={scrollToSection}
        onSettingsClick={() => setIsSettingsOpen(true)}
      />

      {/* Settings Panel */}
      <SettingsSection
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onTournamentsClick={onTournamentsClick}
        onResetClick={onResetClick}
        onQRCodeClick={onQRCodeClick}
        onExportPDFClick={onExportPDFClick}
        onSignOutClick={onSignOutClick}
        onLoginClick={onLoginClick}
        isSyncing={isSyncing}
        lastSyncTime={lastSyncTime}
      />

      {/* Scrollable Sections */}
      <div className="scroll-wizard-container">
        {sections.map((section, index) => {
          if (!section.enabled) return null;

          const SectionComponent = section.component;
          const isHidden = !scrollSnapEnabled && index > 0;

          return (
            <section
              key={section.id}
              ref={(el) => (sectionRefs.current[index] = el)}
              className={`scroll-section ${currentSection === index ? 'active' : ''} ${isHidden ? 'hidden-section' : ''}`}
              id={`section-${section.id}`}
            >
              <div className="section-content">
                <SectionComponent
                  onGenerate={section.id === 'courts' ? handleGenerateTournament : undefined}
                  onResetClick={section.id === 'config' ? onResetClick : undefined}
                />
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ScrollWizardPage;
